/**
 * NotesView.tsx - NotesView - Obsidian-style markdown note-taking with wiki-links
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDatasetView } from '../base/useDatasetView';
import { useViewMetadata } from '../base/useViewMetadata';
import { useBacktrackr } from '@opcentral/backtrackr';
// TODO: Re-enable when editr package is complete
// import { MarkdownEditorAdvanced as MarkdownEditor } from '@opcentral/inputr';
import WikiLinkParser from './WikiLinkParser';
import BacklinksPanel from './BacklinksPanel';
import TagsPanel from './TagsPanel';
import NotesSearch from './NotesSearch';
import './NotesView.css';

/**
 * NotesView Component
 * 
 * @param {Object} props
 * @param {string} props.viewId - Unique view identifier
 * @param {Object} props.datasetContext - Dataset context
 * @param {Object} props.dataContext - Data context
 * @param {Object} props.config - View configuration
 */
const NotesView = ({
  viewId = 'notes-default',
  datasetContext,
  dataContext,
  backtrackrContext: propBacktrackrContext,
  config = {}
}) => {
  // State
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [editorMode, setEditorMode] = useState('wysiwyg'); // 'wysiwyg', 'markdown', 'split'
  const [showBacklinks, setShowBacklinks] = useState(true);
  const [showTags, setShowTags] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Contexts
  const {
    items,
    fieldMappings,
    getField,
    setField,
    updateItem,
    addItem,
    deleteItem
  } = useDatasetView('notes', datasetContext, dataContext);
  
  const hookBacktrackrContext = useBacktrackr();
  const backtrackrContext = propBacktrackrContext || hookBacktrackrContext;
  const { recordChange, undo, redo, canUndo, canRedo } = backtrackrContext || {};
  
  const {
    getMetadata,
    setMetadata,
    getSortOrder,
    setSortOrder
  } = useViewMetadata(viewId, items, updateItem);
  
  // Extract config
  const notesConfig = config.notes || {};
  const defaultMode = notesConfig.editorMode || 'wysiwyg';
  const autoSave = notesConfig.autoSave !== false;
  const autoSaveInterval = notesConfig.autoSaveInterval || 2000;
  const showWordCount = notesConfig.showWordCount !== false;
  const showLineNumbers = notesConfig.showLineNumbers === true;
  const spellCheck = notesConfig.spellCheck !== false;
  
  // Wiki link parser
  const wikiLinkParser = useMemo(() => new WikiLinkParser(items, getField), [items, getField]);
  
  // Get selected note
  const selectedNote = useMemo(() => {
    return items.find(item => item.id === selectedNoteId);
  }, [items, selectedNoteId]);
  
  // Get note content
  const noteContent = useMemo(() => {
    if (!selectedNote) return '';
    return getField(selectedNote, 'content') || '';
  }, [selectedNote, getField]);
  
  // Parse wiki links from content
  const parsedLinks = useMemo(() => {
    return wikiLinkParser.parseLinks(noteContent);
  }, [noteContent, wikiLinkParser]);
  
  // Get backlinks (notes that link to this note)
  const backlinks = useMemo(() => {
    if (!selectedNote) return [];
    
    const noteName = getField(selectedNote, 'name');
    return items.filter(item => {
      if (item.id === selectedNoteId) return false;
      const content = getField(item, 'content') || '';
      return wikiLinkParser.parseLinks(content).some(link => link.title === noteName);
    });
  }, [selectedNote, selectedNoteId, items, getField, wikiLinkParser]);
  
  // Extract tags from content
  const tags = useMemo(() => {
    return wikiLinkParser.extractTags(noteContent);
  }, [noteContent, wikiLinkParser]);
  
  // Get all tags from all notes
  const allTags = useMemo(() => {
    const tagSet = new Set();
    items.forEach(item => {
      const content = getField(item, 'content') || '';
      wikiLinkParser.extractTags(content).forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [items, getField, wikiLinkParser]);
  
  // Search notes
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return items;
    
    const query = searchQuery.toLowerCase();
    return items.filter(item => {
      const name = (getField(item, 'name') || '').toLowerCase();
      const content = (getField(item, 'content') || '').toLowerCase();
      return name.includes(query) || content.includes(query);
    });
  }, [searchQuery, items, getField]);
  
  // Handle note selection
  const handleSelectNote = useCallback((noteId) => {
    setSelectedNoteId(noteId);
  }, []);
  
  // Handle content change
  const handleContentChange = useCallback((newContent) => {
    if (!selectedNoteId) return;
    
    const contentField = fieldMappings['content'];
    if (contentField) {
      updateItem(selectedNoteId, { [contentField]: newContent });
    }
  }, [selectedNoteId, fieldMappings, updateItem]);
  
  // Handle create new note
  const handleCreateNote = useCallback(() => {
    const nameField = fieldMappings['name'];
    const contentField = fieldMappings['content'];
    
    if (!nameField) return;
    
    const newNote = {
      [nameField]: 'Untitled Note',
      [contentField]: ''
    };
    
    const newId = addItem(newNote);
    if (newId) {
      setSelectedNoteId(newId);
    }
  }, [fieldMappings, addItem]);
  
  // Handle delete note
  const handleDeleteNote = useCallback((noteId) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteItem(noteId);
      if (selectedNoteId === noteId) {
        setSelectedNoteId(null);
      }
    }
  }, [deleteItem, selectedNoteId]);
  
  // Handle wiki link click
  const handleWikiLinkClick = useCallback((linkTitle) => {
    // Find note with matching title
    const targetNote = items.find(item => {
      const name = getField(item, 'name');
      return name === linkTitle;
    });
    
    if (targetNote) {
      setSelectedNoteId(targetNote.id);
    } else {
      // Create new note if it doesn't exist
      const nameField = fieldMappings['name'];
      const contentField = fieldMappings['content'];
      
      if (nameField) {
        const newNote = {
          [nameField]: linkTitle,
          [contentField]: ''
        };
        
        const newId = addItem(newNote);
        if (newId) {
          setSelectedNoteId(newId);
        }
      }
    }
  }, [items, getField, fieldMappings, addItem]);
  
  // Auto-save
  useEffect(() => {
    if (!autoSave || !selectedNoteId) return;
    
    const timer = setTimeout(() => {
      // Auto-save is handled by updateItem in handleContentChange
    }, autoSaveInterval);
    
    return () => clearTimeout(timer);
  }, [autoSave, autoSaveInterval, selectedNoteId, noteContent]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      
      // Cmd+N: New note
      if (cmdOrCtrl && e.key === 'n') {
        e.preventDefault();
        handleCreateNote();
      }
      
      // Cmd+F: Search
      if (cmdOrCtrl && e.key === 'f') {
        e.preventDefault();
        setShowSearch(true);
      }
      
      // Cmd+Z: Undo
      if (cmdOrCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo && undo) undo();
      }
      
      // Cmd+Shift+Z or Cmd+Y: Redo
      if (cmdOrCtrl && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        if (canRedo && redo) redo();
      }
      
      // Cmd+E: Toggle editor mode
      if (cmdOrCtrl && e.key === 'e') {
        e.preventDefault();
        setEditorMode(prev => {
          if (prev === 'wysiwyg') return 'markdown';
          if (prev === 'markdown') return 'split';
          return 'wysiwyg';
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreateNote, canUndo, canRedo, undo, redo]);
  
  return (
    <div className="notes-view">
      {/* Sidebar */}
      <div className="notes-sidebar">
        <div className="notes-sidebar-header">
          <h2>Notes</h2>
          <button 
            className="btn-new-note"
            onClick={handleCreateNote}
            title="New Note (Cmd+N)"
          >
            + New
          </button>
        </div>
        
        {/* Search */}
        <div className="notes-search-box">
          <input
            type="text"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="notes-search-input"
          />
        </div>
        
        {/* Notes list */}
        <div className="notes-list">
          {searchResults.map(note => {
            const noteName = getField(note, 'name') || 'Untitled';
            const isSelected = note.id === selectedNoteId;
            
            return (
              <div
                key={note.id}
                className={`note-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelectNote(note.id)}
              >
                <div className="note-item-name">{noteName}</div>
                <div className="note-item-preview">
                  {(getField(note, 'content') || '').substring(0, 100)}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Tags panel */}
        {showTags && (
          <TagsPanel
            tags={allTags}
            onTagClick={(tag) => setSearchQuery(`#${tag}`)}
          />
        )}
      </div>
      
      {/* Main editor */}
      <div className="notes-main">
        {selectedNote ? (
          <>
            <div className="notes-header">
              <input
                type="text"
                className="note-title-input"
                value={getField(selectedNote, 'name') || ''}
                onChange={(e) => {
                  const nameField = fieldMappings['name'];
                  if (nameField) {
                    updateItem(selectedNoteId, { [nameField]: e.target.value });
                  }
                }}
                placeholder="Note title..."
              />
              
              <div className="notes-toolbar">
                <select
                  value={editorMode}
                  onChange={(e) => setEditorMode(e.target.value)}
                  className="editor-mode-select"
                >
                  <option value="wysiwyg">WYSIWYG</option>
                  <option value="markdown">Markdown</option>
                  <option value="split">Split</option>
                </select>
                
                <button
                  onClick={() => handleDeleteNote(selectedNoteId)}
                  className="btn-delete-note"
                  title="Delete note"
                >
                  🗑️
                </button>
              </div>
            </div>
            
            <div className="notes-editor">
              {/* TODO: Replace with MarkdownEditor when editr is complete */}
              <textarea
                value={noteContent}
                onChange={(e) => handleContentChange(e.target.value)}
                placeholder="Start typing your note..."
                style={{ width: '100%', minHeight: '400px', fontFamily: 'monospace', padding: '12px' }}
              />
            </div>
            
            {showWordCount && (
              <div className="notes-footer">
                <span className="word-count">
                  {selectedNote?.content.split(/\s+/).filter(w => w).length} words
                  {noteContent.split(/\s+/).filter(w => w).length} words
                </span>
                <span className="char-count">
                  {noteContent.length} characters
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="notes-empty-state">
            <h3>No note selected</h3>
            <p>Select a note from the sidebar or create a new one</p>
            <button onClick={handleCreateNote} className="btn-create-first-note">
              Create your first note
            </button>
          </div>
        )}
      </div>
      
      {/* Right sidebar - Backlinks & metadata */}
      {selectedNote && showBacklinks && (
        <div className="notes-right-sidebar">
          <BacklinksPanel
            backlinks={backlinks}
            onBacklinkClick={handleSelectNote}
            getField={getField}
          />
          
          <div className="note-metadata">
            <h4>Links in this note</h4>
            <div className="note-links">
              {parsedLinks.map((link, index) => (
                <div
                  key={index}
                  className="note-link"
                  onClick={() => handleWikiLinkClick(link.title)}
                >
                  [[{link.title}]]
                </div>
              ))}
            </div>
            
            <h4>Tags</h4>
            <div className="note-tags">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="note-tag"
                  onClick={() => setSearchQuery(`#${tag}`)}
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesView;
